/**
 * Guard de request de axios para el patrón "id vacío interpolado en la URL".
 *
 * Contexto (TKT-2145): el 2026-08-14 un incidente en producción fue causado
 * por servicios que arman URLs con template strings tipo
 * `` `${this.GRAPHQL_URL}/${id}` `` cuando `id` todavía no estaba resuelto
 * (undefined/null/''), generando 404/405 masivos contra el backend. El
 * hotfix del mismo día (PR #1353) agregó guards puntuales `if (!id)` en
 * tasks.js, occurrences.js, organization.js y dos componentes Vue, pero un
 * grep durante ese hotfix encontró el mismo patrón sin proteger en al menos
 * 9 archivos más de src/services (invoices.js, locations.js, products.js,
 * quickChecks.js, routines.js, sprints.js, suscriptions.js, entre otros).
 *
 * Este módulo es la solución de fondo: se engancha una sola vez en
 * src/boot/axios.js y cubre TODOS los servicios presentes y futuros, sin
 * tener que parchear cada llamada una por una.
 *
 * Diseño deliberadamente conservador (cambio estructural, no un hotfix
 * urgente — debe pasar por revisión y soak normal):
 *
 * - BLOQUEA (cancela la petición y rechaza la promesa) solo cuando la
 *   evidencia es inequívoca:
 *     1) un segmento de path literal "undefined" o "null"
 *        (ej. `/api/tasks/undefined`), o
 *     2) un segmento vacío en medio del path, es decir un "//"
 *        (ej. `/api/tasks//occurrence`).
 * - Un solo "/" al final del path (`/api/tasks/` en vez de `/api/tasks/123`)
 *   NO se bloquea: es genuinamente ambiguo en este proyecto. Ejemplos reales
 *   que terminan en "/" a propósito y NO son un id vacío:
 *   `assignmentEmailRol/`, `forms-module/`, `action-plans-module/`,
 *   `users-module/`, `agenda-module/`, `roles/`, e incluso
 *   `.../config/?type=...` (organization.js) donde el "/" precede a un
 *   querystring, no al final crudo de la URL. Bloquear esto de entrada
 *   rompería tráfico legítimo — en su lugar se deja pasar la petición pero
 *   se emite un console.warn para poder auditar en producción qué tan
 *   seguido ocurre y si vale la pena promoverlo a bloqueo en un cambio
 *   posterior, ya con datos reales.
 * - Cada bloqueo real se loggea (console.error, ver boot/axios.js) con el
 *   motivo y la URL completa.
 * - Para desactivar por completo sin revertir el cambio: ENABLED = false.
 */

// Interruptor único para desactivar el guard completo sin tener que revertir
// el cambio (ej. si aparece un falso positivo en producción y se necesita
// mitigar rápido mientras se ajusta la detección).
export const ENABLED = true

// Endpoints reales de este proyecto que terminan en "/" a propósito (no son
// un `${id}` vacío) — verificado contra src/services/*.js el 2026-08-14.
// Se usa solo para reducir ruido en el console.warn de abajo, NO bloquea
// nada: el caso de "/" final nunca cancela la petición (ver comentario del
// módulo). Si agregas una llamada nueva que termina en "/" a propósito y
// quieres silenciar el warning, agrégala aquí.
const KNOWN_TRAILING_SLASH_ENDPOINTS = [
  '/assignmentEmailRol/',
  '/forms-module/',
  '/action-plans-module/',
  '/users-module/',
  '/agenda-module/',
  '/roles/',
  '/config/'
]

// Segmento de path que es literalmente "undefined" o "null" (con límites de
// "/" o fin de cadena a ambos lados), ej. ".../tasks/undefined" o
// ".../undefined/occurrence".
const UNDEFINED_OR_NULL_SEGMENT = /(^|\/)(undefined|null)(\/|$)/

function stripQueryAndHash (url) {
  return url.split('?')[0].split('#')[0]
}

function stripProtocol (url) {
  return url.replace(/^[a-zA-Z][a-zA-Z\d+.-]*:\/\//, '')
}

/**
 * Analiza una URL y devuelve `{ severity: 'block' | 'warn', reason }` si
 * encuentra un patrón sospechoso, o `null` si la URL se ve bien.
 *
 * `severity: 'block'` = evidencia inequívoca de id vacío, se debe cancelar.
 * `severity: 'warn'`  = patrón ambiguo (ver comentario del módulo), se deja
 * pasar la petición pero se loggea para auditoría.
 */
export function classifyEmptyIdUrl (rawUrl) {
  if (!rawUrl || typeof rawUrl !== 'string') return null

  const path = stripProtocol(stripQueryAndHash(rawUrl))

  if (UNDEFINED_OR_NULL_SEGMENT.test(path)) {
    return { severity: 'block', reason: 'segmento de path literal "undefined"/"null" (probable id sin resolver)' }
  }

  if (path.includes('//')) {
    return { severity: 'block', reason: 'segmento de path vacío ("//") en medio de la URL (probable id vacío)' }
  }

  if (path.endsWith('/')) {
    const isKnownTrailingSlashEndpoint = KNOWN_TRAILING_SLASH_ENDPOINTS.some(suffix => path.endsWith(suffix))
    if (!isKnownTrailingSlashEndpoint) {
      return { severity: 'warn', reason: 'la URL termina en "/" (posible id vacío al final, o endpoint legítimo no listado en KNOWN_TRAILING_SLASH_ENDPOINTS)' }
    }
  }

  return null
}

export class EmptyIdRequestError extends Error {
  constructor (config, reason) {
    const method = (config.method || 'get').toUpperCase()
    super(`[axios-empty-id-interceptor] Petición cancelada antes de salir a red (${reason}): ${method} ${config.url}`)
    this.name = 'EmptyIdRequestError'
    this.isEmptyIdInterceptor = true
    this.config = config
    this.reason = reason
    // Historico: src/api/responser.js antes solo rechazaba cuando `error.response` existia o
    // `error.message === 'Network Error'` -- sin un `.response` armado aqui, este error hubiera
    // caido por el fallthrough de exito. Desde el fix de timeouts (review PR #1383/#201) el
    // interceptor tambien rechaza cualquier error con `isAxiosError: true` sin `.response`, asi
    // que este `.response` falso ya no seria estrictamente necesario para que rechace -- se deja
    // igual (fuera de alcance de ese fix) porque otros consumidores de EmptyIdRequestError pueden
    // seguir leyendo esta forma (status/data/config) como si fuera una respuesta real.
    this.response = {
      status: 0,
      statusText: 'Blocked by empty-id interceptor',
      data: null,
      config
    }
  }
}

/**
 * Revisa `config.url` contra el guard de id vacío.
 * - Si matchea un patrón "block": lanza EmptyIdRequestError (quien la llama
 *   debe atraparlo y rechazar la promesa de axios).
 * - Si matchea un patrón "warn": emite console.warn y retorna normalmente
 *   (la petición sigue su curso).
 * - Si el guard está desactivado (ENABLED = false) o la URL no matchea
 *   nada, no hace nada.
 */
export function assertNoEmptyIdSegment (config) {
  if (!ENABLED || !config) return

  const result = classifyEmptyIdUrl(config.url)
  if (!result) return

  if (result.severity === 'warn') {
    const method = (config.method || 'get').toUpperCase()
    console.warn(`[axios-empty-id-interceptor] URL sospechosa, no bloqueada (${result.reason}): ${method} ${config.url}`)
    return
  }

  throw new EmptyIdRequestError(config, result.reason)
}
